import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Text,
  useToast,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { FaArrowDown, FaArrowUp, FaPaperPlane } from "react-icons/fa";
import isEmail from "validator/lib/isEmail";
import {
  ContactPersonDto,
  ContactSubjectType,
  DocumentSignerMethod,
  PersonDto,
  SignerDto,
  TextDocumentDto,
  useCreateSignedDocumentMutation,
  useCreateSignedTextDocumentMutation,
  useGetOrganizationQuery,
  useListContactPersonsQuery,
  ViewDocumentAsOwnerDto,
  ViewSharedDocumentDto,
} from "../../../../../autogen/bff-api";
import { supplierContactPersons } from "../../../../../common/contactPersons/supplierContactPersons";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { CounterpartyContext } from "../../../../../common/types";
import { isSignable } from "../../../../../common/documents/utils";
import { useApiError } from "../../../../../common/errors/useApiError";
import { DateTimeSelector } from "../../../../../common/input/DateTimeSelectors/DateTimeSelector";
import { MultiSelector } from "../../../../../common/input/Selector/MultiSelector";
import { SelectorValue } from "../../../../../common/input/Selector/SelectorValue";
import { SingleSelector } from "../../../../../common/input/Selector/SingleSelector";
import { generateHTMLFromTextDocument } from "../../../../../common/input/TipTap/downloadTextDocument";
import { ImageReadyState, getImageUploadIds } from "../../../../../common/input/TipTap/pendingImages";
import { ImageStatusProbe } from "./ImageStatusProbe";
import { getPreferredLanguage } from "../../../../../common/local-storage/language";
import { PersonsSelectorForm } from "../../../../../common/persons/PersonsSelectorForm";
import { useSub } from "../../../../../common/subscription/useSub";
import { Explanation } from "../../../../../common/support/Explanation/Explanation";
import { useSkeletons } from "../../../../../common/useSkeletons";
import { displayPersonNameWithEmail } from "../../../view-single/sharing/AddExternalParticipantModal";
import { InvitedSigners } from "./InvitedSigners";

interface Props {
  contractId: string;
  mainDocument: MainDocumentContext;
  documents: (ViewSharedDocumentDto | ViewDocumentAsOwnerDto)[];
  textDocuments: TextDocumentDto[];
  counterpartyContext: CounterpartyContext;
  onSuccess: () => void;
}

export type MainDocumentContext =
  | {
      documentId: string;
      textDocument?: never;
    }
  | {
      documentId?: never;
      textDocument: TextDocumentDto;
    };

interface FormValues {
  mainDocumentId: string;
  attachments: SelectorValue[];
  internalSigners: PersonDto[];
  counterpartySigners: PersonDto[];
  organizationEntryCounterpartySigners: PersonDto[];
  manualSigners?: SignerDto[];
  deadline?: string;
  signatureMethod: DocumentSignerMethod;
}

/* Documents may be signed by 
- Internal users: Persons in the current organization,
- Counterparty organization entry contact persons: Contact persons in a self registered counterparty organization
- Counterparty organization persons: Persons in a registered counterparty organization
- Email users: Any person given by a manually typed email
 */

export const CreateSignedDocument = ({
  contractId,
  mainDocument,
  documents,
  textDocuments,
  counterpartyContext,
  onSuccess,
}: Props) => {
  const toast = useToast();
  const authState = useLoggedInWithOrgContextState();
  const skeletons = useSkeletons();
  const sub = useSub();
  const displayer = useApiError();

  const {
    data: organization,
    error: organizationError,
    isLoading: isLoadingOrganization,
  } = useGetOrganizationQuery(
    counterpartyContext.organizationId ? { orgId: counterpartyContext.organizationId } : skipToken
  );

  const { data: orgContacts } = useListContactPersonsQuery(
    counterpartyContext.organizationId
      ? { subjectType: ContactSubjectType.RegisteredOrganization, subjectId: counterpartyContext.organizationId }
      : skipToken
  );

  const linkedContactPersons: ContactPersonDto[] | undefined = useMemo(
    () => (organization ? supplierContactPersons(orgContacts?.contacts ?? [], organization.linkedEntries) : undefined),
    [organization, orgContacts?.contacts]
  );

  const [createSignedDocument, { isLoading: isCreatingSignedDocument }] = useCreateSignedDocumentMutation();
  const [createSignedTextDocument, { isLoading: isCreatingSignedTextDocument }] = useCreateSignedTextDocumentMutation();

  const signatureMethods: { [key in DocumentSignerMethod]: string } = {
    Handwritten: t("Handwritten"),
    NoBankId: t("BankID"),
  };

  const organizationEntryContactPersons: PersonDto[] | undefined = useMemo(
    () =>
      counterpartyContext.contactPersons?.map((p) => ({
        id: p.email,
        firstName: p.name,
        email: p.email,
      })),
    [counterpartyContext.contactPersons]
  );

  const signableDocuments = useMemo(() => documents.filter((document) => isSignable(document)), [documents]);

  const methods = useForm<FormValues>({
    defaultValues: {
      mainDocumentId: mainDocument.documentId ?? mainDocument.textDocument.id,
      attachments: [],
      manualSigners: [],
      signatureMethod: sub.hasBankIdAccess ? DocumentSignerMethod.NoBankId : DocumentSignerMethod.Handwritten,
    },
  });

  const mapSigners = useCallback(
    ({
      internalSigners,
      counterpartySigners,
      manualSigners,
      signatureMethod,
      organizationEntryCounterpartySigners,
    }: {
      internalSigners?: PersonDto[];
      counterpartySigners?: PersonDto[];
      manualSigners?: SignerDto[];
      organizationEntryCounterpartySigners?: PersonDto[];
      signatureMethod: DocumentSignerMethod;
    }): SignerDto[] => {
      return [
        ...(manualSigners?.map((s) => ({
          ...s,
          signatureMethod,
        })) ?? []),
        ...(internalSigners?.map((s) => ({
          firstName: s.firstName ?? "",
          lastName: s.lastName ?? "",
          email: s.email.trim(),
          signatureMethod,
        })) ?? []),
        ...(counterpartySigners?.map((s) => ({
          firstName: s.firstName ?? "",
          lastName: s.lastName ?? "",
          email: s.email.trim(),
          signatureMethod,
        })) ?? []),
        ...(organizationEntryCounterpartySigners?.map((s) => ({
          firstName: s.firstName ?? "",
          lastName: s.lastName ?? "",
          email: s.email.trim(),
          signatureMethod,
        })) ?? []),
      ];
    },
    []
  );

  const buildAttachments = useCallback(
    (attachmentIds?: string[]) =>
      attachmentIds?.map((id) => {
        const textDoc = textDocuments.find((d) => d.id === id);
        if (textDoc) {
          return { textDocument: { textDocumentId: textDoc.id, htmlContent: generateHTMLFromTextDocument(textDoc.text) } };
        }
        return { documentId: id };
      }),
    [textDocuments]
  );

  const sendForSigning = useCallback(
    async (values: { mainDocumentId: string; attachmentIds?: string[]; signers: SignerDto[]; deadline?: string }) => {
      if (!values.signers.length) throw new Error("Signers are not defined");

      const mainDocumentId = signableDocuments.find((s) => s.id === values.mainDocumentId)?.id;
      if (!mainDocumentId) throw new Error("Signable document with provided id not found");
      try {
        await createSignedDocument({
          contractId,
          createSignedDocumentRequest: {
            mainDocumentId,
            attachments: buildAttachments(values.attachmentIds),
            signers: values.signers,
            language: getPreferredLanguage(),
            deadline: values.deadline,
          },
        }).unwrap();
        toast({ title: t("The document is sent for signing"), status: "success" });
        onSuccess();
      } catch (e) {
        displayer.trigger(e);
      }
    },
    [buildAttachments, contractId, createSignedDocument, displayer, onSuccess, signableDocuments, toast]
  );

  const sendTextDocumentForSigning = useCallback(
    async (values: { mainDocumentId: string; attachmentIds?: string[]; signers: SignerDto[]; deadline?: string }) => {
      if (!values.signers.length) throw new Error("Signers are not defined");

      const textDocument = textDocuments.find((d) => d.id === values.mainDocumentId);
      if (!textDocument) throw new Error("Text document with provided id not found");
      await createSignedTextDocument({
        contractId,
        createSignedTextDocumentRequest: {
          textDocumentId: textDocument.id,
          htmlContent: generateHTMLFromTextDocument(textDocument.text),
          attachments: buildAttachments(values.attachmentIds),
          signers: values.signers,
          language: getPreferredLanguage(),
          deadline: values.deadline,
        },
      })
        .unwrap()
        .catch((e) => {
          displayer.trigger(e);
          throw e;
        });
      toast({ title: t("The document is sent for signing"), status: "success" });
      onSuccess();
    },
    [buildAttachments, contractId, createSignedTextDocument, displayer, onSuccess, textDocuments, toast]
  );

  const send = async ({
    mainDocumentId,
    attachments,
    internalSigners,
    counterpartySigners,
    organizationEntryCounterpartySigners,
    manualSigners,
    deadline,
    signatureMethod,
  }: FormValues) => {
    const document = documents.find((document) => document.id === mainDocumentId);
    const textDocument = textDocuments.find((document) => document.id === mainDocumentId);

    const attachmentIds = attachments.map((a) => a.value);

    const signers = mapSigners({
      internalSigners,
      counterpartySigners,
      organizationEntryCounterpartySigners,
      manualSigners,
      signatureMethod,
    });

    if (document) {
      sendForSigning({ mainDocumentId, attachmentIds, signers, deadline });
    } else if (textDocument) {
      sendTextDocumentForSigning({ mainDocumentId, attachmentIds, signers, deadline });
    }
  };

  const mainDocumentId = methods.watch("mainDocumentId");
  const attachments = methods.watch("attachments");
  const manualSigners = methods.watch("manualSigners");
  const internalSigners = methods.watch("internalSigners");
  const counterpartySigners = methods.watch("counterpartySigners");
  const organizationEntryCounterpartySigners = methods.watch("organizationEntryCounterpartySigners");

  const selectedTextDocuments = useMemo(() => {
    const ids = [mainDocumentId, ...attachments.map((a) => a.value)];
    return ids.map((id) => textDocuments.find((td) => td.id === id)).filter((td): td is TextDocumentDto => !!td);
  }, [mainDocumentId, attachments, textDocuments]);

  const confirmedUploadIds = useMemo(
    () =>
      new Set(
        selectedTextDocuments.flatMap((td) =>
          (td.documents ?? []).map((doc) => doc?.fromUploadId).filter((id): id is string => !!id),
        ),
      ),
    [selectedTextDocuments],
  );

  const imageProbes = useMemo(
    () =>
      selectedTextDocuments.flatMap((td) =>
        getImageUploadIds(td)
          .filter((uploadId) => !confirmedUploadIds.has(uploadId))
          .map((uploadId) => ({ textDocumentId: td.id, uploadId })),
      ),
    [selectedTextDocuments, confirmedUploadIds],
  );

  const [imageStatusByUploadId, setImageStatusByUploadId] = useState<Record<string, ImageReadyState>>({});
  const reportImageStatus = useCallback(
    (uploadId: string, state: ImageReadyState) => {
      setImageStatusByUploadId((prev) => (prev[uploadId] === state ? prev : { ...prev, [uploadId]: state }));
    },
    [],
  );

  const hasPendingImageDocuments = imageProbes.some((p) => imageStatusByUploadId[p.uploadId] !== "ready");

  const moveAttachment = (index: number, direction: -1 | 1) => {
    const updated = [...attachments];
    const targetIndex = index + direction;
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
    methods.setValue("attachments", updated);
  };

  const hasSigners = !!(
    manualSigners?.length ||
    internalSigners?.length ||
    counterpartySigners?.length ||
    organizationEntryCounterpartySigners?.length
  );

  const allSignersAreValid =
    manualSigners?.every(
      (e) => isEmail(e.email.trim()) && e.firstName.trim().length > 1 && e.lastName.trim().length > 1
    ) ?? true;

  if (organizationError) return <Text>{t("Something went wrong")}...</Text>;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(send)}>
        <Flex flexDirection="column" gap="3">
          {/* Document selection */}
          <Box>
            <Text fontWeight="medium" fontSize="sm" mb="2">
              {t("Document to be signed")}
            </Text>
            <FormControl id="documentToBeSigned" isInvalid={methods.formState.errors.mainDocumentId !== undefined}>
              <Controller
                name="mainDocumentId"
                control={methods.control}
                render={({ field: { onChange, value } }) => {
                  const selectedOption =
                    documents.find((d) => d.id === value) ?? textDocuments?.find((t) => t.id === value);
                  return (
                    <SingleSelector
                      size="sm"
                      value={selectedOption ? { label: selectedOption.name, value } : undefined}
                      options={[
                        ...documents.map((d) => ({ label: d.name, value: d.id })),
                        ...(textDocuments?.map((d) => ({ label: d.name, value: d.id })) ?? []),
                      ]}
                      onChange={(value) => onChange(value?.value)}
                      placeholder={{
                        text: t("Select document"),
                        color: "inherit",
                      }}
                      noOptionsProvidedMessage={t("No documents have been uploaded") ?? ""}
                      noMatchingOptionsMessage={t("No matching documents") ?? ""}
                    />
                  );
                }}
              />
              <FormErrorMessage>
                {methods.formState.errors.mainDocumentId && methods.formState.errors.mainDocumentId.message}
              </FormErrorMessage>
            </FormControl>
          </Box>

          {mainDocumentId && (
            <>
              {/* Attachments */}
              <Box>
                <Text fontWeight="medium" fontSize="sm" mb="2">
                  {t("Attachments")}
                </Text>
                <FormControl id="attachments" isInvalid={methods.formState.errors.attachments !== undefined}>
                  <Controller
                    name="attachments"
                    control={methods.control}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <MultiSelector
                          size="sm"
                          value={value}
                          options={[
                            ...documents.map((e) => ({ label: e.name, value: e.id })),
                            ...(textDocuments?.map((d) => ({ label: d.name, value: d.id })) ?? []),
                          ].filter((d) => d.value !== mainDocumentId)}
                          onChange={(value) => onChange(value)}
                          placeholder={{ text: t("Select document"), color: "inherit" }}
                          noOptionsAvailableMessage={t("No matching documents") ?? ""}
                        />
                      );
                    }}
                  />
                  <FormErrorMessage>
                    {methods.formState.errors.attachments && methods.formState.errors.attachments.message}
                  </FormErrorMessage>
                </FormControl>
                {attachments.length > 1 && (
                  <Flex flexDirection="column" pt="3">
                    <Text fontSize="xs" color="smMuted" mb="1">
                      {t("Attachment order")}
                    </Text>
                    {attachments.map((attachment, index) => (
                      <Flex
                        key={attachment.value}
                        alignItems="center"
                        gap="2"
                        px="1"
                        py="1"
                        borderBottom={index < attachments.length - 1 ? "1px solid" : undefined}
                        borderColor="smBorder"
                      >
                        <Text fontSize="sm" flex="1" noOfLines={1} color="smMuted">
                          {index + 1}. {attachment.label}
                        </Text>
                        <IconButton
                          aria-label={t("Move up")}
                          icon={<Icon as={FaArrowUp} />}
                          size="xs"
                          variant="ghost"
                          isDisabled={index === 0}
                          onClick={() => moveAttachment(index, -1)}
                        />
                        <IconButton
                          aria-label={t("Move down")}
                          icon={<Icon as={FaArrowDown} />}
                          size="xs"
                          variant="ghost"
                          isDisabled={index === attachments.length - 1}
                          onClick={() => moveAttachment(index, 1)}
                        />
                      </Flex>
                    ))}
                  </Flex>
                )}
              </Box>

              {/* Signers */}
              <Box border="1px solid" borderColor="smBorder" p="4" rounded="md">
                <PersonsSelectorForm<"internalSigners", FormValues>
                  organizationIds={[authState.selectedOrg.id]}
                  label={t("Internal signers")}
                  control={methods.control}
                  name="internalSigners"
                  placeholderText={t("Select internal signers")}
                  errorMessage={methods.formState.errors.internalSigners?.message}
                />
                {counterpartyContext.organizationId && (
                  <Flex pt="5">
                    <PersonsSelectorForm<"counterpartySigners", FormValues>
                      organizationIds={[counterpartyContext.organizationId]}
                      additionalPersons={linkedContactPersons?.map((p) => ({
                        id: p.email,
                        firstName: p.name,
                        email: p.email,
                        phoneNumber: p.phoneNumber,
                      }))}
                      label={t("Counterparty signers")}
                      control={methods.control}
                      name="counterpartySigners"
                      placeholderText={t("Select counterparty signers")}
                      errorMessage={methods.formState.errors.counterpartySigners?.message}
                      isLoading={isLoadingOrganization}
                    />
                  </Flex>
                )}
                {organizationEntryContactPersons && (
                  <FormControl
                    id="organizationEntryCounterpartySigners"
                    isInvalid={methods.formState.errors.counterpartySigners !== undefined}
                    pt="5"
                  >
                    <FormLabel>{t("Counterparty signers")}</FormLabel>
                    <Controller
                      name="organizationEntryCounterpartySigners"
                      control={methods.control}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <MultiSelector
                            size="sm"
                            value={value?.map((u) => ({
                              label: displayPersonNameWithEmail(u),
                              value: u.id,
                            }))}
                            options={
                              organizationEntryContactPersons.map((u) => ({
                                label: displayPersonNameWithEmail(u),
                                value: u.id,
                              })) ?? []
                            }
                            onChange={(o) => {
                              const persons = organizationEntryContactPersons.filter((p) =>
                                o.map((o) => o.value).includes(p.id)
                              );
                              onChange(persons);
                            }}
                            placeholder={{ text: t("Select counterparty signers"), color: "inherit" }}
                            noOptionsAvailableMessage={t("No matching users") ?? ""}
                          />
                        );
                      }}
                    />
                    <FormErrorMessage>
                      {methods.formState.errors.counterpartySigners &&
                        methods.formState.errors.counterpartySigners.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
                <Box pt="5">
                  <FormLabel htmlFor="signedDocuments" pb="2">
                    {t("Other signers")}
                  </FormLabel>
                  <InvitedSigners />
                </Box>
              </Box>

              {/* Settings */}
              <Box>
                <Text fontWeight="medium" fontSize="sm" mb="2">
                  {t("Settings")}
                </Text>
                <Flex flexDirection="column" gap="3">
                  {sub.hasBankIdAccess && (
                    <FormControl
                      id="signatureMethod"
                      isInvalid={methods.formState.errors.signatureMethod !== undefined}
                    >
                      <FormLabel fontSize="sm">{t("Signature method")}</FormLabel>
                      <Controller
                        name="signatureMethod"
                        control={methods.control}
                        render={({ field: { onChange, value } }) => {
                          return (
                            <SingleSelector
                              size="sm"
                              value={{ value: value as string, label: signatureMethods[value] }}
                              options={Object.keys(signatureMethods).map((value) => ({
                                label: signatureMethods[value as DocumentSignerMethod],
                                value,
                              }))}
                              onChange={(value) => onChange(value?.value)}
                              placeholder={{
                                text: t("Select signature method"),
                                color: "inherit",
                              }}
                            />
                          );
                        }}
                      />
                      <FormErrorMessage>
                        {methods.formState.errors.signatureMethod &&
                          methods.formState.errors.signatureMethod.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                  <FormControl id="deadline" isInvalid={methods.formState.errors.deadline !== undefined}>
                    <FormLabel fontSize="sm" htmlFor="deadline">
                      {t("Deadline")}
                    </FormLabel>
                    <Controller
                      name="deadline"
                      control={methods.control}
                      rules={{
                        validate: (value) =>
                          !value ||
                          new Date(value) > new Date() ||
                          (t("The deadline must be in the future") ?? ""),
                      }}
                      render={({ field: { onChange, value } }) => {
                        return <DateTimeSelector defaultDate={value} onChange={onChange} />;
                      }}
                    />
                    <FormErrorMessage>
                      {methods.formState.errors.deadline && methods.formState.errors.deadline.message}
                    </FormErrorMessage>
                  </FormControl>
                </Flex>
              </Box>

              {/* Sticky submit */}
              <Flex
                justifyContent="end"
                position="sticky"
                bottom="0"
                pt="4"
                pb="6"
                bg="smBackground"
                borderTop="1px solid"
                borderColor="smBorder"
                mt="1"
                zIndex={1}
              >
                <Explanation
                  fontSize="sm"
                  enabled={!allSignersAreValid || hasPendingImageDocuments}
                  text={
                    hasPendingImageDocuments
                      ? t(
                          "One or more of the selected documents contain images that are not fully processed yet. Please wait a moment and try again."
                        )
                      : `${t(`Make sure all signers have a valid email, first name and last name before sending the document for signing`)}.`
                  }
                >
                  <Button
                    type="submit"
                    variant="solid"
                    size="sm"
                    colorScheme="blue"
                    isLoading={isCreatingSignedDocument || isCreatingSignedTextDocument}
                    isDisabled={!allSignersAreValid || !hasSigners || hasPendingImageDocuments}
                    leftIcon={<Icon as={FaPaperPlane} w="15px" h="15px" />}
                  >
                    {t("Send out for signing")}
                  </Button>
                </Explanation>
              </Flex>
            </>
          )}
        </Flex>
        {imageProbes.map((p) => (
          <ImageStatusProbe
            key={`${p.textDocumentId}:${p.uploadId}`}
            textDocumentId={p.textDocumentId}
            uploadId={p.uploadId}
            onState={reportImageStatus}
          />
        ))}
      </form>
    </FormProvider>
  );
};
